import { t } from "@lingui/macro";
import { AspectRatio } from "@reactive-resume/ui";
import { cn, curatedTemplatesList } from "@reactive-resume/utils";
import { motion } from "framer-motion";
import { useEffect, useState } from "react";

import { useAudioChatStore } from "@/client/stores/audioChatStore";

import { recordLegacyMessages, recordLegacyStateUpdate } from "./_utils/legacyTranscriptBridge";
import { TypingText } from "./typingText";

type Message = {
  type: "user" | "ai";
  content: string;
};
type messageType = Message[];

export const TemplateSelection = () => {
  const [hasTemplatePromptStarted, setHasTemplatePromptStarted] = useState(false);
  const [showTemplateGrid, setShowTemplateGrid] = useState(false);
  const [showSelectButton, setShowSelectButton] = useState(true);
  const [messages, setMessages] = useState<messageType>([]);
  const step = useAudioChatStore((state) => state.step);
  const currentTemplate = useAudioChatStore((state) => state.currentTemplate);
  const setStep = useAudioChatStore((state) => state.setStep);
  const updateCurrentTemplate = useAudioChatStore((state) => state.updateCurrentTemplate);

  const handleTemplateChange = (template: string) => {
    updateCurrentTemplate(template);
    recordLegacyStateUpdate("template", template);
  };

  const handleSubmit = () => {
    const selectedMessage: Message = {
      type: "user",
      content: t`Use the ${currentTemplate} template.`,
    };

    setMessages((prevMessages) => [...prevMessages, selectedMessage]);
    recordLegacyMessages([selectedMessage]);
    setShowTemplateGrid(false);
    setShowSelectButton(false);
    setStep("GENERATE");
  };

  useEffect(() => {
    if (step === "TEMPLATES") {
      const initialMessages: messageType = [
        {
          type: "ai",
          content: t`Please select the resume template that best reflects your style and preferences.`,
        },
      ];
      setHasTemplatePromptStarted(true);
      setShowSelectButton(true);
      setShowTemplateGrid(true);
      setMessages(initialMessages);
      recordLegacyMessages(initialMessages);
    }
  }, [step]);

  return hasTemplatePromptStarted ? (
    <div className="mb-6">
      {messages.map((message, index) => (
        <div key={`jd-${index}`}>
          <div
            className={cn(
              "mx-1 mb-3 max-w-[80%] rounded-lg px-4 py-2 text-base shadow-md",
              message.type === "user"
                ? "ml-auto bg-blue-100 text-blue-800"
                : "bg-gray-100 text-gray-800",
            )}
          >
            {message.type === "ai" ? (
              <div className="flex items-center gap-2">
                <div className="grow">
                  <TypingText message={message.content} />
                </div>
              </div>
            ) : (
              message.content
            )}
          </div>
        </div>
      ))}

      {showTemplateGrid && (
        <div className="grid max-h-[25rem] overflow-auto">
          <div className="m-2 grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 lg:gap-5">
            {curatedTemplatesList.map((template, index) => (
              <AspectRatio key={template} ratio={1 / 1.4142}>
                <motion.button
                  type="button"
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1, transition: { delay: index * 0.1 } }}
                  whileTap={{ scale: 0.98, transition: { duration: 0.1 } }}
                  onClick={() => handleTemplateChange(template)}
                  className={cn(
                    "relative size-full cursor-pointer overflow-hidden rounded-sm text-left ring-primary transition-all hover:ring-2 focus:outline-none focus:ring-2 focus:ring-offset-2",
                    currentTemplate === template && "ring-2",
                  )}
                  aria-pressed={currentTemplate === template}
                >
                  <img
                    src={`/templates/jpg/${template}.jpg`}
                    alt={template}
                    className="size-full rounded-sm object-cover"
                  />

                  <div className="absolute inset-x-0 bottom-0 h-32 w-full bg-gradient-to-b from-transparent to-background/80">
                    <p className="absolute inset-x-0 bottom-2 text-center font-bold capitalize text-primary">
                      {template}
                    </p>
                  </div>
                </motion.button>
              </AspectRatio>
            ))}
          </div>
        </div>
      )}

      {showSelectButton && (
        <div className="ml-2 mt-4 flex">
          <button
            type="button"
            className="genai-gradient-4 flex cursor-pointer items-center gap-1 rounded-lg px-3 py-2 shadow-md transition-all duration-500 hover:scale-105 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2"
            onClick={handleSubmit}
          >
            <span className="text-base font-medium text-zinc-800">{t`Select Template`}</span>
          </button>
        </div>
      )}
    </div>
  ) : null;
};
